import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

/** Fixed budget categories with their display order and type */
export const BUDGET_CATEGORIES = [
  { key: 'ConnectionFee', sortOrder: 0, type: 'income' as const },
  { key: 'Revenue', sortOrder: 1, type: 'income' as const },
  { key: 'GoodsCost', sortOrder: 2, type: 'cost' as const },
  { key: 'Personnel', sortOrder: 3, type: 'cost' as const },
  { key: 'OtherCosts', sortOrder: 4, type: 'cost' as const }
] as const;

export type BudgetCategory = typeof BUDGET_CATEGORIES[number]['key'];

export const INCOME_CATEGORIES = BUDGET_CATEGORIES.filter(c => c.type === 'income');
export const COST_CATEGORIES = BUDGET_CATEGORIES.filter(c => c.type === 'cost');

/** Unified budget line used by both WO and Project levels */
export interface BudgetLine {
  category: string;
  budgetAmount: number;
  estimatedHours?: number;
  notes?: string;
  actualAmount: number;
  actualHours: number;
  variance: number;
}

/** Summary totals */
export interface BudgetSummary {
  lines: BudgetLine[];
  totalBudget: number;
  totalActual: number;
  totalVariance: number;
  totalEstimatedHours: number;
  totalActualHours: number;
  coveragePercent?: number;
}

/** Save request emitted to parent */
export interface BudgetSaveEvent {
  lines: { category: string; budgetAmount: number; estimatedHours?: number; notes?: string }[];
}

@Component({
  selector: 'app-budget-card',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './budget-card.component.html',
  styleUrls: ['./budget-card.component.scss']
})
export class BudgetCardComponent implements OnInit, OnChanges {
  /** Budget summary from the API */
  @Input() budgetSummary: BudgetSummary | null = null;

  /** Whether data is loading */
  @Input() loading = false;

  /** Whether save is in progress */
  @Input() saving = false;

  /** Emitted when user saves budget */
  @Output() budgetSave = new EventEmitter<BudgetSaveEvent>();

  /** Emitted when component needs data refresh */
  @Output() refresh = new EventEmitter<void>();

  editing = false;
  editLines: { category: string; budgetAmount: number; estimatedHours: number | null; notes: string }[] = [];

  /** The fixed category keys for iteration */
  categories = BUDGET_CATEGORIES;
  incomeCategories = INCOME_CATEGORIES;
  costCategories = COST_CATEGORIES;

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {
    // Edit lines are populated when user clicks edit (startEdit)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['budgetSummary'] && this.budgetSummary && !this.editing) {
      // Don't reset edit lines while user is editing
    }
  }

  /** Get display label for a category key */
  getCategoryLabel(key: string): string {
    return this.translateService.instant(`Budget.category_${key}`);
  }

  /** Get line by category from the summary */
  getLine(category: string): BudgetLine | undefined {
    return this.budgetSummary?.lines.find(l => l.category === category);
  }

  /** Start editing — populate edit lines from existing data */
  startEdit(): void {
    this.editLines = BUDGET_CATEGORIES.map(cat => {
      const existing = this.getLine(cat.key);
      return {
        category: cat.key,
        budgetAmount: existing?.budgetAmount ?? 0,
        estimatedHours: existing?.estimatedHours ?? null,
        notes: existing?.notes ?? ''
      };
    });
    this.editing = true;
  }

  /** Cancel editing */
  cancelEdit(): void {
    this.editing = false;
  }

  /** Save budget */
  save(): void {
    const lines = this.editLines.map(l => ({
      category: l.category,
      budgetAmount: l.budgetAmount || 0,
      estimatedHours: l.estimatedHours || undefined,
      notes: l.notes?.trim() || undefined
    }));
    this.budgetSave.emit({ lines });
  }

  /** Format currency for display */
  formatCurrency(value: number | undefined | null): string {
    if (value == null) return '–';
    return new Intl.NumberFormat('nb-NO', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    }).format(value);
  }

  /** Get CSS class for variance value */
  getVarianceClass(variance: number): string {
    if (variance > 0) return 'text-success';
    if (variance < 0) return 'text-danger';
    return '';
  }

  /** Check if category is income type */
  isIncomeCategory(key: string): boolean {
    return key === 'ConnectionFee' || key === 'Revenue';
  }

  /** Sum budget for income categories from summary lines */
  get incomeBudget(): number {
    return this.budgetSummary?.lines
      .filter(l => this.isIncomeCategory(l.category))
      .reduce((s, l) => s + l.budgetAmount, 0) ?? 0;
  }

  /** Sum actual for income categories from summary lines */
  get incomeActual(): number {
    return this.budgetSummary?.lines
      .filter(l => this.isIncomeCategory(l.category))
      .reduce((s, l) => s + l.actualAmount, 0) ?? 0;
  }

  /** Sum budget for cost categories from summary lines */
  get costBudget(): number {
    return this.budgetSummary?.lines
      .filter(l => !this.isIncomeCategory(l.category))
      .reduce((s, l) => s + l.budgetAmount, 0) ?? 0;
  }

  /** Sum actual for cost categories from summary lines */
  get costActual(): number {
    return this.budgetSummary?.lines
      .filter(l => !this.isIncomeCategory(l.category))
      .reduce((s, l) => s + l.actualAmount, 0) ?? 0;
  }

  /** Net margin budget from edit lines */
  get editIncome(): number {
    return this.editLines.filter(l => this.isIncomeCategory(l.category))
      .reduce((s, l) => s + (l.budgetAmount || 0), 0);
  }

  /** Net cost budget from edit lines */
  get editCost(): number {
    return this.editLines.filter(l => !this.isIncomeCategory(l.category))
      .reduce((s, l) => s + (l.budgetAmount || 0), 0);
  }

  /** Total budget from edit lines (net = income - costs) */
  get editTotal(): number {
    return this.editIncome - this.editCost;
  }
}
